import { EventSource } from "eventsource";
import { getBackendUrl, getHeaders, mapErrorToMessage } from "../axios";
import NotificationManager from "../../common/notification/NotificationManager";
import { API_CALL_NOTIFICATION } from "../constants/notification";

export interface EventsConfig<TMessage = unknown> {
  paramPath: string;
  openCallback?: () => void;
  messageCallback: (message: TMessage) => void;
  errorCallback?: (event: EventsError) => void;
  replaceExisting?: boolean;
}

export interface EventsError {
  type?: string;
  message?: string;
  /** Numeric readyState snapshot (`EventSource.CONNECTING|OPEN|CLOSED`) taken when the error fired. */
  readyState?: number;
  /** HTTP status code, when the underlying failure was an HTTP response (e.g. 401/503). */
  code?: number;
  /**
   * True when the `eventsource` client reached `readyState === EventSource.CLOSED`, i.e. it has
   * given up permanently and will not auto-reconnect. Callers should treat this as a terminal
   * failure requiring an explicit re-subscribe; non-terminal errors keep the existing recovery.
   */
  terminal?: boolean;
}

let eventSource: EventSource | null = null;

function closeEventSource(source: EventSource): void {
  source.onopen = null;
  source.onmessage = null;
  source.onerror = null;
  source.close();
}

function resolveEventSourceUrl(paramPath: string): string {
  if (/^https?:\/\//i.test(paramPath)) {
    return paramPath;
  }

  const backendUrl = getBackendUrl();
  if (!backendUrl) {
    return paramPath;
  }

  return `${backendUrl.replace(/\/$/, "")}/${paramPath.replace(/^\//, "")}`;
}

export const subscribeToHost = async <TMessage = unknown>(config: EventsConfig<TMessage>): Promise<() => void> => {
  console.log("initializing new SEE connection to ", config.paramPath);

  const replaceExisting = config.replaceExisting ?? true;
  if (replaceExisting && eventSource) {
    closeEventSource(eventSource);
    eventSource = null;
    console.log("old SEE connection closed");
  }

  let headers = undefined;
  try {
    headers = await getHeaders(false);
  } catch (e) {
    const messageAndTitle = mapErrorToMessage(e);
    NotificationManager.addNotification({
      title: messageAndTitle.title,
      message: messageAndTitle.message,
      key: API_CALL_NOTIFICATION,
      type: "warning",
      closable: true,
      removeOnClose: true,
      isTemporary: false,
    });
  }

  if (headers) {
    try {
      const source = new EventSource(resolveEventSourceUrl(config.paramPath), {
        fetch: (input, init) =>
          fetch(input, {
            ...init,
            headers: { ...init.headers, ...headers },
          }),
      });

      if (replaceExisting) {
        eventSource = source;
      }

      source.onopen = () => {
        config.openCallback?.();
      };

      source.onmessage = (ev) => {
        try {
          config.messageCallback(JSON.parse(ev.data) as TMessage);
        } catch (e) {
          console.error(`Error parsing event data: ${ev.data}`);
          console.error(e);
          config.errorCallback?.({ type: "error", message: "Failed to parse data" });
        }
      };

      source.onerror = (event) => {
        // Read readyState off the live source (not the event) so terminal detection reflects the
        // eventsource client's own state machine, per its documented CLOSED/OPEN/CONNECTING semantics.
        const readyState = source.readyState;
        config.errorCallback?.({
          type: event.type,
          message: event.message,
          code: event.code,
          readyState,
          terminal: readyState === EventSource.CLOSED,
        });
      };

      console.log("new SEE connection ready");
      return () => {
        closeEventSource(source);
        if (eventSource === source) {
          eventSource = null;
        }
      };
    } catch (e) {
      console.error(`error creating EventSource `, e);
      config.errorCallback?.({ type: "error", message: "Failed to create EventSource", terminal: true });
    }
  } else {
    config.errorCallback?.({ type: "error", message: "Failed to get EventSource headers", terminal: true });
  }

  return () => undefined;
};
